import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react';
import { cn } from '../lib/cn';

export interface ChipProps extends HTMLAttributes<HTMLSpanElement> {
  /** `sm` is the ride-card attribute chip; `md` is the profile preference chip. */
  size?: 'sm' | 'md';
  children: ReactNode;
}

/** Read-only attribute chip: "Quiet ride", "No smoking", "Music welcome". */
export function Chip({ size = 'sm', className, children, ...rest }: ChipProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full bg-hover font-semibold text-ink-2',
        size === 'sm' ? 'px-2.5 py-1 text-mini' : 'px-[11px] py-[5px] text-caption',
        className,
      )}
      {...rest}
    >
      {children}
    </span>
  );
}

export interface SelectChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  selected?: boolean;
  children: ReactNode;
}

/**
 * The selectable chip behind every single-choice control in the product:
 * departure window, minimum rating, seat count, chattiness. Selected state is
 * a ngahere border over a ngahere tint.
 */
export function SelectChip({
  selected = false,
  className,
  type = 'button',
  children,
  ...rest
}: SelectChipProps) {
  return (
    <button
      type={type}
      aria-pressed={selected}
      className={cn(
        'flex-1 cursor-pointer rounded-md border px-1.5 py-2 text-center text-meta font-semibold',
        'transition-colors duration-fast ease-standard',
        selected
          ? 'border-ngahere bg-ngahere-tint text-ngahere'
          : 'border-line bg-surface text-ink-2 hover:bg-hover',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

export interface ChoiceRowProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  selected?: boolean;
  children: ReactNode;
}

/**
 * A full-width radio row — the pickup-point picker in the booking card. The
 * ring is drawn rather than using a native radio so it matches the export.
 */
export function ChoiceRow({
  selected = false,
  className,
  type = 'button',
  children,
  ...rest
}: ChoiceRowProps) {
  return (
    <button
      type={type}
      role="radio"
      aria-checked={selected}
      className={cn(
        'flex w-full cursor-pointer items-center gap-2.5 rounded-lg border px-[13px] py-[11px] text-left',
        'transition-colors duration-fast ease-standard',
        selected ? 'border-ngahere bg-ngahere-tint' : 'border-line bg-surface hover:bg-hover',
        className,
      )}
      {...rest}
    >
      <span
        className={cn(
          'h-4 w-4 flex-none rounded-full border-2',
          selected ? 'border-ngahere bg-ngahere' : 'border-line-radio bg-surface',
        )}
      />
      <span className="text-body-sm font-medium">{children}</span>
    </button>
  );
}
